import DuLogger from './DuLogger';

const CACHE_CONFIG_KEY = 'cacheSettings';
const SESSION_KEY = '_session';
const LOG_KEY = 'DU_CACHE:: ';

var configLoaded = false;

var cacheSettings = {
    lifespanInMilliseconds: 3600000,
    cacheVersion: process.env.REACT_APP_VERSION,
    cacheKeys: []
};

const defaultCacheFunc = async () => {
    DuLogger.error(`${LOG_KEY}Not Setup!!!`);
};

let cacheGlobal = !!window ? window : global;

let setPermanentMethod =
    cacheGlobal && cacheGlobal.localStorage
        ? async (key, value) => {
              cacheGlobal.localStorage.setItem(key, value);
          }
        : defaultCacheFunc;
let readPermanentMethod =
    cacheGlobal && cacheGlobal.localStorage
        ? async (key) => {
              return new Promise(async (resolve) => {
                  resolve(cacheGlobal.localStorage.getItem(key));
              });
          }
        : defaultCacheFunc;
let deletePermanentMethod =
    cacheGlobal && cacheGlobal.localStorage
        ? async (key) => {
              cacheGlobal.localStorage.removeItem(key);
          }
        : defaultCacheFunc;

let setTemporaryMethod =
    cacheGlobal && cacheGlobal.sessionStorage
        ? async (key, value) => {
              cacheGlobal.sessionStorage.setItem(key, value);
          }
        : defaultCacheFunc;
let readTemporaryMethod =
    cacheGlobal && cacheGlobal.sessionStorage
        ? async (key) => {
              return new Promise(async (resolve) => {
                  resolve(cacheGlobal.sessionStorage.getItem(key));
              });
          }
        : defaultCacheFunc;
let deleteTemporaryMethod =
    cacheGlobal && cacheGlobal.sessionStorage
        ? async (key) => {
              cacheGlobal.sessionStorage.removeItem(key);
          }
        : defaultCacheFunc;

let cacheIsSetup = cacheGlobal && cacheGlobal.localStorage && cacheGlobal.sessionStorage;

const setupCache = async (setPermanentFunc, readPermanentFunc, deletePermanentFunc, setTemporaryFunc, readTemporaryFunc, deleteTemporaryFunc) => {
    if (!setTemporaryFunc) {
        setTemporaryFunc = setPermanentFunc;
    }

    if (!readTemporaryFunc) {
        readTemporaryFunc = readPermanentFunc;
    }

    if (!deleteTemporaryFunc) {
        deleteTemporaryFunc = deletePermanentFunc;
    }

    if (
        !verifyFunction(setPermanentFunc) ||
        !verifyFunction(readPermanentFunc) ||
        !verifyFunction(deletePermanentFunc) ||
        !verifyFunction(setTemporaryFunc) ||
        !verifyFunction(readTemporaryFunc) ||
        !verifyFunction(deleteTemporaryFunc)
    ) {
        cacheIsSetup = false;
        DuLogger.error(`${LOG_KEY}Unable to find a required function for cache to function. No cache can be saved!`);
        return;
    }

    setPermanentMethod = setPermanentFunc;
    readPermanentMethod = readPermanentFunc;
    deletePermanentMethod = deletePermanentFunc;

    setTemporaryMethod = setTemporaryFunc;
    readTemporaryMethod = readTemporaryFunc;
    deleteTemporaryMethod = deleteTemporaryFunc;

    await configure();
};

const verifyFunction = async (func) => {
    if (typeof func !== 'function') {
        return false;
    }

    return true;
};

const configure = async () => {
    if (!configLoaded && cacheIsSetup) {
        var cachedSettingsString = (await readPermanentMethod(CACHE_CONFIG_KEY)) || '';

        if (cachedSettingsString.length <= 0) {
            configLoaded = true;
            return;
        }

        var cachedSettings = JSON.parse(cachedSettingsString);

        if (cachedSettings) {
            cacheSettings = cachedSettings;

            if (process.env.REACT_APP_VERSION !== cacheSettings.cacheVersion) {
                clearAllCache();
                cacheSettings.cacheVersion = process.env.REACT_APP_VERSION;
            }
        } else {
            await setPermanentMethod(CACHE_CONFIG_KEY, JSON.stringify(cacheSettings));
        }

        configLoaded = true;
    }
};

const clearAllCache = async () => {
    for (let key of cacheSettings.cacheKeys) {
        clearCache(key);
    }

    cacheSettings.cacheKeys = [];
    await setPermanentMethod(CACHE_CONFIG_KEY, JSON.stringify(cacheSettings));
};

const addKeyToIndex = async (key, isPermanentStorage) => {
    if (!isPermanentStorage) {
        key = key + SESSION_KEY;
    }

    if (cacheSettings.cacheKeys.includes(key)) {
        return;
    }

    cacheSettings.cacheKeys.push(key);

    await setPermanentMethod(CACHE_CONFIG_KEY, JSON.stringify(cacheSettings));
};

const saveCache = async (key, data, isPermanentStorage = true) => {
    await configure();

    if (!configLoaded) {
        DuLogger.error(`${LOG_KEY}Config not able to load, please fix the config!`);
        return false;
    }

    var cacheItem = {
        time: Date.now(),
        data: data
    };

    addKeyToIndex(key, isPermanentStorage);

    if (isPermanentStorage) {
        await setPermanentMethod(key, JSON.stringify(cacheItem));
    } else {
        await setTemporaryMethod(key, JSON.stringify(cacheItem));
    }

    return true;
};

const clearCache = async (key) => {
    await configure();

    if (!configLoaded) {
        DuLogger.error(`${LOG_KEY}Config not able to load, please fix the config!`);
        return;
    }

    if (key.includes(SESSION_KEY)) {
        key = key.replace(SESSION_KEY, '');
        await deleteTemporaryMethod(key);
    } else {
        await deletePermanentMethod(key);
    }
};

const checkCache = async (key, lifespanOverrideInMilliseconds, useGracePeriod, isPermanentStorage = true) => {
    await configure();

    if (!configLoaded) {
        DuLogger.error(`${LOG_KEY}Config not able to load, please fix the config!`);
        return false;
    }

    var cacheItem = isPermanentStorage ? await readPermanentMethod(key) : await readTemporaryMethod(key);

    if (!cacheItem) {
        return false;
    }

    cacheItem = JSON.parse(cacheItem);

    if (!cacheItem) {
        clearCache(key);

        return false;
    }

    var timeDifference = Date.now() - cacheItem.time;
    var lifespan = lifespanOverrideInMilliseconds > 0 ? lifespanOverrideInMilliseconds : cacheSettings.lifespanInMilliseconds;

    if (timeDifference > lifespan + (useGracePeriod ? 10000 : 0)) {
        clearCache(key);

        return false;
    }

    return true;
};

const getCache = async (key, lifespanOverrideInMilliseconds, isPermanentStorage = true) => {
    if (await checkCache(key, lifespanOverrideInMilliseconds, true, isPermanentStorage)) {
        return JSON.parse(isPermanentStorage ? await readPermanentMethod(key) : await readTemporaryMethod(key)).data;
    }

    return null;
};

const DuCache = {
    Setup: setupCache,
    Save: saveCache,
    Clear: clearCache,
    Check: checkCache,
    Get: getCache
};

export default DuCache;
